import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { Copy, ExternalLink, Globe, Loader } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { ThemedText } from './ThemedText';
import { Button } from './Button';
import { organizationService } from '../api';
import { demoSubdomainUrl } from '../utils/demoStudio';

const SectionTitle = styled.h3`
  font-size: 0.85rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  margin-top: 0;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  padding-bottom: ${({ theme }) => theme.spacing.sm};
  border-bottom: 2px solid ${({ theme }) => theme.colors.border};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DomainBox = styled.div`
  background-color: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  flex-wrap: wrap;
  code {
    font-family: monospace;
    color: ${({ theme }) => theme.colors.tint};
    word-break: break-all;
    font-size: ${({ theme }) => theme.typography.sizes.default};
  }
`;

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 10px;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ $tone }) => ($tone === 'live' ? '#0b6b3a' : $tone === 'off' ? '#8a6d1a' : '#666')};
  background: ${({ $tone }) => ($tone === 'live' ? 'rgba(34,197,94,0.15)' : $tone === 'off' ? 'rgba(245,196,66,0.2)' : 'rgba(120,120,120,0.15)')};
`;

const Hint = styled(ThemedText).attrs({ type: 'small' })`
  color: ${({ theme }) => theme.colors.muted};
  display: block;
  margin-top: ${({ theme }) => theme.spacing.sm};
`;

const LinkRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.typography.sizes.small};
`;

const InlineAction = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.tint};
  cursor: pointer;
  padding: 0;
  font-size: inherit;
  &:hover { text-decoration: underline; }
`;

/**
 * SuperAdmin Domain tab (Demo Studio editor, edit mode): shows the demo's pretty subdomain
 * ({slug}.aqrate.ca) and provisions its Cloudflare DNS record. Everything downstream (nginx wildcard
 * vhost + origin cert + /demos/{slug}) already exists server-side — DNS is the only per-demo step,
 * and it is also attempted automatically when the demo is created.
 */
const DemoDomainPanel = ({ orgId, slug }) => {
  const theme = useTheme();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [provisioning, setProvisioning] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const data = await organizationService.getDemoDomain(orgId);
      setStatus(data);
    } catch (error) {
      console.error('Failed to load demo domain status:', error);
      toast.error(error?.message || 'Failed to load domain status');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { refresh(); }, [refresh]);

  const handleProvision = async () => {
    setProvisioning(true);
    try {
      const data = await organizationService.provisionDemoDomain(orgId);
      setStatus(data);
      if (data.exists) toast.success('Domain is set up');
      else toast.error(data.message || 'Cloudflare did not create the record');
    } catch (error) {
      console.error('Failed to provision demo domain:', error);
      toast.error(error?.message || 'Failed to set up the domain');
    } finally {
      setProvisioning(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('URL copied');
  };

  const hostname = status?.hostname || `${slug || '‹slug›'}.aqrate.ca`;
  const url = demoSubdomainUrl(slug);
  const tone = status?.exists ? 'live' : status?.configured ? 'off' : 'none';
  const label = status?.exists ? 'Live via Cloudflare' : status?.configured ? 'Not provisioned' : 'Not configured';

  return (
    <>
      <SectionTitle theme={theme}>Demo subdomain</SectionTitle>

      <DomainBox theme={theme}>
        <Globe size={16} />
        <code>{hostname}</code>
        {loading ? <Loader size={14} /> : <StatusPill $tone={tone}>{label}</StatusPill>}
      </DomainBox>

      {status?.exists && (
        <LinkRow theme={theme}>
          <InlineAction theme={theme} as="a" href={url} target="_blank" rel="noopener noreferrer">
            <ExternalLink size={13} /> Open {hostname}
          </InlineAction>
          <InlineAction theme={theme} type="button" onClick={() => copy(url)}>
            <Copy size={13} /> Copy URL
          </InlineAction>
        </LinkRow>
      )}

      {!loading && status && !status.configured && (
        <Hint theme={theme}>
          {status.message || 'Cloudflare is not configured on this server.'} The demo remains reachable at the
          standard /demos/{slug} URL.
        </Hint>
      )}
      {!loading && status?.configured && !status.exists && (
        <Hint theme={theme}>
          {status.message
            ? `Last attempt: ${status.message}`
            : 'The DNS record has not been created yet (it is attempted automatically when a demo is created).'}
        </Hint>
      )}

      <div style={{ marginTop: theme.spacing.md }}>
        <Button
          type="button"
          onClick={handleProvision}
          disabled={provisioning || loading || !status?.configured || status?.exists}
        >
          {provisioning ? 'Setting up…' : status?.exists ? 'Domain is set up' : 'Set up domain'}
        </Button>
      </div>
      <Hint theme={theme}>
        Creates a proxied CNAME {hostname} → api.aqrate.ca in Cloudflare. The server’s wildcard vhost and
        certificate already cover every demo subdomain.
      </Hint>
    </>
  );
};

export default DemoDomainPanel;
export { DemoDomainPanel };
